/* eslint-disable complexity, max-classes-per-file */

'use no memo';

import React, { useContext, useEffect } from 'react';

import { Restaurant } from '@local/api';

import { UserContext } from 'context/UserContext';
import { RestaurantContext } from 'context/RestaurantContext';
import { LocalOrderType, OrderContext } from 'context/OrderContext';
import { MenuContext } from 'context/MenuContext';
import { ModalContext } from 'context/ModalContext';
import { useSelectedAddressStore } from 'store';

import { isAbortError } from 'helpers/api';
import { logException } from 'helpers/loggers';
import { MODAL_TYPE } from 'helpers/modals';
import { FULFILLMENT_METHODS, isOrderAddressValid } from 'helpers/order';

import useHandleInvalidOrder from 'hooks/useHandleInvalidOrder';
import useEffectEvent from 'hooks/useEffectEvent';

import { UserDataType } from 'types/customer';
import { MenuDataType } from 'types/menu';

export const ValidateOrderContext = React.createContext<
  (opts?: {
    controller?: AbortController;
    forceValidation?: boolean;
    isLoggingOut?: boolean;
    validateForCheckout?: boolean;
  }) => void
>(() => {
  throw new Error('not implemented');
});

export type ValidateOrderContextType = React.ContextType<
  typeof ValidateOrderContext
>;

class InvalidAddressError extends Error {}
class InvalidMenuError extends Error {}

function preValidateOrder(
  order: LocalOrderType,
  _user: UserDataType,
  restaurant: Restaurant,
  menu: MenuDataType,
  isLoggingOut?: boolean
) {
  const selectedAddress = useSelectedAddressStore.getState().address;

  if (
    order.fulfill_method === FULFILLMENT_METHODS.delivery &&
    (!isOrderAddressValid(selectedAddress) ||
      (isLoggingOut && !!restaurant.delivery_only))
  ) {
    throw new InvalidAddressError();
  } else if (!menu.id) {
    // menu id would be missing when menu call fails and someone tries to make a change on
    // their order (i.e. changing fulfillment)
    throw new InvalidMenuError();
  }
}

/**
 * wraps validateOrder from OrderProvider, automatically
 * pulling in the user, restaurant, and menu from the respective contexts.
 *
 * This is necessary as the order context depends on data from these
 * contexts (user, restaurant, and menu) and vice versa.
 */
export default function ValidateOrderProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user } = useContext(UserContext);
  const { restaurant, fulfillmentEta } = useContext(RestaurantContext);
  const { order, validateOrder } = useContext(OrderContext);
  const { menu } = useContext(MenuContext);
  const handleInvalidOrder = useHandleInvalidOrder();

  const { openModal } = useContext(ModalContext);

  const wrappedValidateOrder = useEffectEvent<ValidateOrderContextType>(
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    async ({
      controller,
      isLoggingOut = false,
      validateForCheckout = false,
      forceValidation = false,
    } = {}) => {
      if (
        !order ||
        !order?.fulfill_method || // in the case of logging in/out on Confirmation or Membership
        !user ||
        !restaurant ||
        !menu?.id ||
        (!order?.pending_validation &&
          !(isLoggingOut || validateForCheckout || forceValidation))
      ) {
        return;
      }

      try {
        preValidateOrder(order, user, restaurant, menu, isLoggingOut);
      } catch (e) {
        if (e instanceof InvalidAddressError) {
          openModal(MODAL_TYPE.fulfillmentDetails, {
            fulfillmentMethod: order.fulfill_method,
            noCloseOverlay: restaurant.delivery_only,
            shouldDefaultToPickup: !restaurant.delivery_only,
          });
        } else if (e instanceof InvalidMenuError) {
          logException({
            message:
              'ValidateOrderProvider: InvalidMenuError during pre-validation',
          });
          openModal(MODAL_TYPE.dialog, {
            onConfirm: () => {
              window.location.reload();
            },
            noCloseOverlay: true,
            isAlert: true,
          });
        }

        return;
      }

      const response = await validateOrder(
        user,
        restaurant,
        menu,
        validateForCheckout ? fulfillmentEta : undefined,
        controller
      );

      if (response.error && !isAbortError(response.error)) {
        // @ts-expect-error error response unknown
        await handleInvalidOrder('validate', response.data, response.error);
      }
    }
  );

  useEffect(() => {
    const controller = new AbortController();

    wrappedValidateOrder({ controller });

    return () => {
      controller.abort();
    };
  }, [menu, restaurant, user, order]);

  return (
    <ValidateOrderContext.Provider value={wrappedValidateOrder}>
      {children}
    </ValidateOrderContext.Provider>
  );
}
